import type { VehicleBrandRecord, VehicleBrandVideo } from "../../api/vehicles";
import "./SimpleBrandCard.css";

function escapeHtml(value: unknown): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function safeHttpUrl(value: unknown): string {
  const raw = String(value ?? "").trim();
  if (!raw) return "";

  try {
    const parsed = new URL(raw);
    return parsed.protocol === "http:" || parsed.protocol === "https:"
      ? parsed.toString()
      : "";
  } catch {
    return "";
  }
}

function formatViews(value: number | null | undefined): string {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) return "";
  return `${new Intl.NumberFormat(undefined, { notation: "compact", maximumFractionDigits: 1 }).format(value)} views`;
}

function formatPublished(value: string): string {
  const published = new Date(value);
  if (Number.isNaN(published.getTime())) return "";

  const seconds = Math.max(0, Math.floor((Date.now() - published.getTime()) / 1000));
  const units: Array<[number, Intl.RelativeTimeFormatUnit]> = [
    [31536000, "year"],
    [2592000, "month"],
    [604800, "week"],
    [86400, "day"],
    [3600, "hour"],
    [60, "minute"],
  ];
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });

  for (const [size, unit] of units) {
    if (seconds >= size) {
      return formatter.format(-Math.floor(seconds / size), unit);
    }
  }
  return "just now";
}

function renderVideo(video: VehicleBrandVideo): string {
  const videoUrl = safeHttpUrl(video.youtube_url);
  const thumbnailUrl = safeHttpUrl(video.thumbnail_url);
  if (!videoUrl || !thumbnailUrl) return "";

  const meta = [formatViews(video.view_count), formatPublished(video.published_at)]
    .filter(Boolean)
    .join(" · ");

  return `
    <a
      class="simple-brand-video"
      href="${escapeHtml(videoUrl)}"
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Open ${escapeHtml(video.title)} on YouTube"
    >
      <div class="simple-brand-video__thumb-wrap">
        <img
          class="simple-brand-video__thumb"
          src="${escapeHtml(thumbnailUrl)}"
          alt=""
          loading="lazy"
        />
      </div>
      <div class="simple-brand-video__copy">
        <strong>${escapeHtml(video.title)}</strong>
        <span>${escapeHtml(video.channel_title || "YouTube")}</span>
        ${meta ? `<span>${escapeHtml(meta)}</span>` : ""}
      </div>
    </a>
  `;
}

export function renderSimpleBrandCard(brand: VehicleBrandRecord): string {
  const imageUrl = safeHttpUrl(brand.image);
  const sourceUrl = safeHttpUrl(brand.source?.url);
  const sourceLabel = brand.source?.label || "Wikipedia";
  const hasImage = Boolean(imageUrl);
  const videos = Array.isArray(brand.videos) ? brand.videos.slice(0, 3) : [];
  const videoHtml = videos.map(renderVideo).filter(Boolean).join("");

  return `
    <article class="simple-brand-card${hasImage ? " has-image" : ""}">
      <div class="simple-brand-card__copy">
        <div class="simple-brand-card__name">${escapeHtml(brand.name)}</div>
        <div class="simple-brand-card__type">Company</div>
        <p class="simple-brand-card__description">${escapeHtml(brand.description)}</p>
        ${
          sourceUrl
            ? `<a
                 class="simple-brand-card__source"
                 href="${escapeHtml(sourceUrl)}"
                 target="_blank"
                 rel="noopener noreferrer"
               >Source ${escapeHtml(sourceLabel)}</a>`
            : `<div class="simple-brand-card__source">Source ${escapeHtml(sourceLabel)}</div>`
        }
      </div>

      ${
        hasImage
          ? `<div class="simple-brand-card__media">
               <img
                 class="simple-brand-card__image"
                 src="${escapeHtml(imageUrl)}"
                 alt="${escapeHtml(brand.image_alt || brand.name)}"
                 loading="lazy"
                 referrerpolicy="no-referrer"
               />
             </div>`
          : ""
      }
    </article>

    ${
      videoHtml
        ? `<section class="simple-brand-videos" aria-label="${escapeHtml(brand.name)} videos">
             <div class="simple-brand-videos__heading">VIDEOS</div>
             <div class="simple-brand-videos__grid">${videoHtml}</div>
             <div class="simple-brand-videos__source">Video metadata and thumbnails provided by YouTube</div>
           </section>`
        : ""
    }
  `;
}
